import { Button, Grid, Stack, TextField, Typography } from '@mui/material'
import { FC, useMemo, useState } from 'react'
import { Contacts, GBAddressCountry, Location, USAddressCountry } from '../../../../api/models'
import { useForm } from 'react-hook-form'
import { numberToWord } from '../../../../utils/utils'
import { InterruptionPanel } from '../../../monitoring-moderation/CameraOverviewPage/InterruptionPanel'
import { formatPhoneNumberForDisplay } from './phoneNumberUtils'

interface EmergencyContactsEditViewProps {
  location: Location
  contacts: Contacts
  onCancel: () => void
  onSave: (contacts: Contacts) => void
  loading?: boolean
}

export const EmergencyContactsEditView: FC<EmergencyContactsEditViewProps> = ({ location, contacts, onCancel, onSave, loading = false }) => {
  const maxNumberOfPrimaryContacts = 2
  const isGBLocation = location?.address?.country === GBAddressCountry.GB
  const fillAboveContactsMessage = 'Enter contact information in first available contact slot above'

  const maxNumberOfSecondaryContacts = 5

  const [showInterruptionPanel, setShowInterruptionPanel] = useState(false)

  const phoneCountry = (location.address?.country as GBAddressCountry | USAddressCountry) ?? USAddressCountry.US

  const formattedContacts = useMemo<Contacts>(
    () => ({
      ...contacts,
      primaryContacts: contacts.primaryContacts.map(contact => ({
        ...contact,
        phone: {
          ...contact.phone,
          number: contact.phone.number ? formatPhoneNumberForDisplay(contact.phone.number, phoneCountry) : ''
        }
      })),
      secondaryContacts: contacts.secondaryContacts.map(contact => ({
        ...contact,
        phone: {
          ...contact.phone,
          number: contact.phone.number ? formatPhoneNumberForDisplay(contact.phone.number, phoneCountry) : ''
        }
      }))
    }),
    [contacts, phoneCountry]
  )

  const {
    handleSubmit,
    register,
    formState: { errors }
  } = useForm<Contacts>({ values: formattedContacts })

  const handleCancel = () => {
    setShowInterruptionPanel(true)
  }

  const handleInterruptionConfirm = (confirmed: boolean) => {
    if (confirmed) {
      // Exit the edit view
      onCancel()
    }
    setShowInterruptionPanel(false)
  }

  // Sanitize phone number by removing spaces, dashes, parentheses, and plus signs before saving
  const sanitizePhoneNumber = (value: string) => value.replace(/[\s\-+()]/g, '')

  const validatePhoneNumberForCountry = (value: string) => {
    if (!value) {
      return true
    }

    if (!/^[0-9()+\-\s]+$/.test(value)) {
      return 'Only numbers, spaces, and -, +, (, ) are allowed'
    }

    const digitsOnlyValue = value.replace(/\D/g, '')

    if (phoneCountry === USAddressCountry.US) {
      const isValidUSDigits = digitsOnlyValue.length === 10 || (digitsOnlyValue.length === 11 && digitsOnlyValue.startsWith('1'))
      return isValidUSDigits ? true : 'US phone number must be 10 digits, or 11 digits if it starts with 1'
    }

    if (phoneCountry === GBAddressCountry.GB) {
      const isValidGBDigits = digitsOnlyValue.length === 11 || (digitsOnlyValue.length === 12 && digitsOnlyValue.startsWith('44'))
      return isValidGBDigits ? true : 'GB phone number must be 11 digits, or 12 digits if it starts with 44'
    }

    return true
  }

  const hasValue = (value?: string) => !!value?.trim()
  const hasFirstAndLastName = (name?: string) => /\s+/.test(name?.trim() ?? '')

  const hasPrimaryContactData = (contact: Contacts['primaryContacts'][number] | undefined) => {
    return !!(hasValue(contact?.name?.firstName) || hasValue(contact?.name?.lastName) || hasValue(contact?.phone?.number))
  }

  const isPrimaryContactBlank = (contact: Contacts['primaryContacts'][number] | undefined) => !hasPrimaryContactData(contact)

  const hasBlankPrimaryContactAbove = (formValues: Contacts, index: number) => {
    for (let priorIndex = 0; priorIndex < index; priorIndex++) {
      if (isPrimaryContactBlank(formValues.primaryContacts?.[priorIndex])) {
        return true
      }
    }
    return false
  }

  const hasSecondaryContactData = (contact: Contacts['secondaryContacts'][number] | undefined) => {
    return !!(hasValue(contact?.name) || hasValue(contact?.phone?.number))
  }

  const isSecondaryContactBlank = (contact: Contacts['secondaryContacts'][number] | undefined) => !hasSecondaryContactData(contact)

  const hasBlankSecondaryContactAbove = (formValues: Contacts, index: number) => {
    for (let priorIndex = 0; priorIndex < index; priorIndex++) {
      if (isSecondaryContactBlank(formValues.secondaryContacts?.[priorIndex])) {
        return true
      }
    }
    return false
  }

  const handleSave = (formContacts: Contacts) => {
    const sanitizedContacts: Contacts = {
      ...formContacts,
      primaryContacts: formContacts.primaryContacts.map(contact => ({
        ...contact,
        phone: {
          ...contact.phone,
          number: sanitizePhoneNumber(contact.phone?.number)
        }
      })),
      secondaryContacts: formContacts.secondaryContacts.map(contact => ({
        ...contact,
        phone: {
          ...contact.phone,
          number: sanitizePhoneNumber(contact.phone?.number)
        }
      }))
    }

    onSave(sanitizedContacts)
  }

  return (
    <form onSubmit={handleSubmit(handleSave)}>
      <Stack
        direction="column"
        spacing={'var(--spacing-m)'}
        sx={{ backgroundColor: 'var(--bg-surface)', borderRadius: 'var(--border-radius-l)', padding: 'var(--spacing-m)' }}
        data-testid="emergency-contacts-edit-view"
      >
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Stack direction="column">
            <Typography variant={'h2'} fontSize={'var(--size-xl)'} sx={{ fontWeight: '700', paddingBlockEnd: 'var(--spacing-s)' }}>
              Edit Emergency Contacts
            </Typography>
            <Typography>Manage the people who will be contacted if there's an alarm.</Typography>
          </Stack>
          <Stack direction="row" spacing={'var(--spacing-xs)'} sx={{ justifyContent: 'flex-end', padding: '0 1.5rem', position: 'sticky' }}>
            <Button
              data-testid="back-to-edit-button"
              variant="outlined"
              onClick={() => handleCancel()}
              sx={{ height: '3rem', fontSize: 'var(--size-s)' }}
            >
              Cancel
            </Button>
            <Button
              data-testid="edit-contact-save-button"
              variant="contained"
              color="primary"
              type="submit"
              loading={loading}
              sx={{ height: '3rem', fontSize: 'var(--size-s)' }}
            >
              Save
            </Button>
          </Stack>
        </Stack>
        <Stack direction="column" spacing={'var(--spacing-s)'} sx={{ paddingBlockEnd: 'var(--spacing-m)' }}>
          <Typography sx={{ fontWeight: '700', fontSize: 'var(--size-m)' }}>Primary Contacts</Typography>
          <Typography>
            We will call up to {numberToWord(maxNumberOfPrimaryContacts)} people before requesting emergency services to avoid dispatch for false
            alarms.
          </Typography>
          <Grid container spacing={'var(--spacing-s)'} sx={{ marginTop: 'var(--spacing-l)', width: '100%' }}>
            {Array.from({ length: maxNumberOfPrimaryContacts }, (_, index) => {
              const isGbSecondPrimaryContact = isGBLocation && index === 1

              return (
                <Grid size={12} key={index}>
                  <Stack direction="column" spacing={'var(--spacing-s)'}>
                    <Typography sx={{ fontWeight: '700', fontSize: 'var(--size-m)' }}>Contact {index + 1}</Typography>
                    <Stack direction="row" spacing={'var(--spacing-l)'}>
                      {!isGbSecondPrimaryContact && (
                        <Stack direction="column" spacing={'var(--spacing-2xs)'} sx={{ flex: 1 }} data-testid="emergency-contacts-first-name-editor">
                          <TextField
                            autoComplete="off"
                            label="First Name"
                            error={!!errors.primaryContacts?.[index]?.name?.firstName}
                            helperText={errors.primaryContacts?.[index]?.name?.firstName?.message}
                            {...register(`primaryContacts.${index}.name.firstName`, {
                              validate: (value, formValues) => {
                                if (index === 0) {
                                  return value ? true : 'First name is required'
                                }
                                const contact = formValues.primaryContacts?.[index]
                                if (hasBlankPrimaryContactAbove(formValues, index) && hasPrimaryContactData(contact)) {
                                  return fillAboveContactsMessage
                                }
                                const hasOtherFields = contact?.name?.lastName || contact?.phone?.number
                                if (hasOtherFields && !value) {
                                  return 'First name is required'
                                }
                                return true
                              }
                            })}
                            slotProps={{
                              htmlInput: { ['data-testid']: `emergency-contacts-first-name-input-${index}` }
                            }}
                          />
                        </Stack>
                      )}
                      {!isGbSecondPrimaryContact && (
                        <Stack direction="column" spacing={'var(--spacing-2xs)'} sx={{ flex: 1 }} data-testid="emergency-contacts-last-name-editor">
                          <TextField
                            autoComplete="off"
                            label="Last Name"
                            error={!!errors.primaryContacts?.[index]?.name?.lastName}
                            helperText={errors.primaryContacts?.[index]?.name?.lastName?.message}
                            {...register(`primaryContacts.${index}.name.lastName`, {
                              validate: (value, formValues) => {
                                if (index === 0) {
                                  return value ? true : 'Last name is required'
                                }
                                const contact = formValues.primaryContacts?.[index]
                                if (hasBlankPrimaryContactAbove(formValues, index) && hasPrimaryContactData(contact)) {
                                  return fillAboveContactsMessage
                                }
                                const hasOtherFields = contact?.name?.firstName || contact?.phone?.number
                                if (hasOtherFields && !value) {
                                  return 'Last name is required'
                                }
                                return true
                              }
                            })}
                            slotProps={{
                              htmlInput: { ['data-testid']: `emergency-contacts-last-name-input-${index}` }
                            }}
                          />
                        </Stack>
                      )}
                      <Stack direction="column" spacing={'var(--spacing-2xs)'} sx={{ flex: 1 }} data-testid="emergency-contacts-phone-number-editor">
                        <TextField
                          autoComplete="off"
                          label="Phone Number"
                          error={!!errors.primaryContacts?.[index]?.phone?.number}
                          helperText={errors.primaryContacts?.[index]?.phone?.number?.message}
                          {...register(`primaryContacts.${index}.phone.number`, {
                            validate: (value, formValues) => {
                              if (index === 0) {
                                if (!value) return 'Phone number is required'
                              } else {
                                const contact = formValues.primaryContacts?.[index]
                                if (hasBlankPrimaryContactAbove(formValues, index) && hasPrimaryContactData(contact)) {
                                  return fillAboveContactsMessage
                                }
                                const hasOtherFields = contact?.name?.firstName || contact?.name?.lastName
                                if (hasOtherFields && !value) {
                                  return 'Phone number is required'
                                }
                              }
                              return validatePhoneNumberForCountry(value)
                            }
                          })}
                          slotProps={{
                            htmlInput: { ['data-testid']: `emergency-contacts-phone-number-input-${index}` }
                          }}
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                </Grid>
              )
            })}
          </Grid>
        </Stack>
        <Stack direction="column" spacing={'var(--spacing-xs)'} sx={{ paddingBlockEnd: 'var(--spacing-m)' }}>
          <Typography sx={{ fontWeight: '700', fontSize: 'var(--size-m)' }}>Secondary Contacts</Typography>
          <Typography>We will call up to {numberToWord(maxNumberOfSecondaryContacts)} people after requesting emergency services.</Typography>
          <Grid container spacing={2} sx={{ marginTop: 'var(--spacing-l)', width: '100%' }}>
            {Array.from({ length: maxNumberOfSecondaryContacts }, (_, index) => {
              return (
                <Grid size={12} key={index}>
                  <Stack direction="column" spacing={'var(--spacing-s)'}>
                    <Typography sx={{ fontWeight: '700', fontSize: 'var(--size-m)' }}>Contact {index + 1}</Typography>
                    <Stack direction="row" spacing={'var(--spacing-3xl)'}>
                      <Stack direction="column" spacing={'var(--spacing-2xs)'} sx={{ flex: 1 }}>
                        <TextField
                          autoComplete="off"
                          label="Full Name"
                          error={!!errors.secondaryContacts?.[index]?.name}
                          helperText={errors.secondaryContacts?.[index]?.name?.message}
                          {...register(`secondaryContacts.${index}.name`, {
                            validate: (value, formValues) => {
                              const contact = formValues.secondaryContacts?.[index]
                              if (hasBlankSecondaryContactAbove(formValues, index) && hasSecondaryContactData(contact)) {
                                return fillAboveContactsMessage
                              }
                              if (hasValue(value) && !hasFirstAndLastName(value)) {
                                return 'First and last names are required (separated by a space)'
                              }
                              const hasOtherFields = contact?.phone?.number
                              if (hasOtherFields && !hasValue(value)) {
                                return 'Name is required'
                              }
                              return true
                            }
                          })}
                        />
                      </Stack>
                      <Stack direction="column" spacing={'var(--spacing-2xs)'} sx={{ flex: 1, marginLeft: 'var(--spacing-m)' }}>
                        <TextField
                          autoComplete="off"
                          label="Phone Number"
                          error={!!errors.secondaryContacts?.[index]?.phone?.number}
                          helperText={errors.secondaryContacts?.[index]?.phone?.number?.message}
                          {...register(`secondaryContacts.${index}.phone.number`, {
                            validate: (value, formValues) => {
                              const contact = formValues.secondaryContacts?.[index]
                              if (hasBlankSecondaryContactAbove(formValues, index) && hasSecondaryContactData(contact)) {
                                return fillAboveContactsMessage
                              }
                              const hasOtherFields = contact?.name
                              if (hasOtherFields && !value) {
                                return 'Phone number is required'
                              }
                              return validatePhoneNumberForCountry(value)
                            }
                          })}
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                </Grid>
              )
            })}
          </Grid>
        </Stack>
        <InterruptionPanel
          open={showInterruptionPanel}
          prompt="Exit Editing"
          details="All progress will be discarded if you leave now."
          firstOption="Exit"
          secondOption="Stay Here"
          onClose={() => handleInterruptionConfirm(false)}
          onSelection={handleInterruptionConfirm}
        />
      </Stack>
    </form>
  )
}
